import { ActionIcon, Affix, Center, FlexProps, Loader } from "@mantine/core"
import {
  InfiniteData,
  UseInfiniteQueryResult,
  UseSuspenseInfiniteQueryResult,
} from "@tanstack/react-query"
import { Pagination } from "../utils/Pagination"
import { ScrollContainer } from "./scroll-container"
import { IconChevronDown } from "@tabler/icons-react"

type InfiniteQueryResultFor<TItem> =
  | UseSuspenseInfiniteQueryResult<InfiniteData<Pagination<TItem>, unknown>>
  | UseInfiniteQueryResult<InfiniteData<Pagination<TItem>, unknown>>

type ItemFromInfiniteQueryResult<TQueryResult> =
  TQueryResult extends InfiniteQueryResultFor<infer TItem> ? TItem : never

interface RenderItemProps<T> {
  entity: T
  index: number
  containerRef: React.RefObject<HTMLDivElement | null>
}

interface PageDirection {
  fetch: () => Promise<unknown>
  hasMore: boolean
  isFetching: boolean
}

export interface BidirectionalInfiniteListProps<
  TQueryResult extends InfiniteQueryResultFor<unknown>,
> extends Omit<FlexProps, "children"> {
  queryResult: TQueryResult
  getItemId: (entity: ItemFromInfiniteQueryResult<TQueryResult>) => string
  renderItem: (
    props: RenderItemProps<ItemFromInfiniteQueryResult<TQueryResult>>
  ) => React.ReactNode
  divider?: (
    previous: ItemFromInfiniteQueryResult<TQueryResult> | undefined,
    current: ItemFromInfiniteQueryResult<TQueryResult>
  ) => React.ReactNode
  older: PageDirection
  newer: PageDirection
  /** The last read item. It is centered when the list first opens. */
  initialAnchorId?: string
  anchorMarker?: {
    beforeId?: string
    afterId?: string
    content: React.ReactNode
  }
  sendedMessage?: boolean
  fetchFirstPage: () => void
}

const BOTTOM_THRESHOLD = 32

export function BidirectionalInfiniteList<
  TQueryResult extends InfiniteQueryResultFor<unknown>,
>({
  queryResult,
  getItemId,
  renderItem,
  divider,
  older,
  newer,
  initialAnchorId,
  anchorMarker,
  sendedMessage,
  fetchFirstPage,
  ...props
}: BidirectionalInfiniteListProps<TQueryResult>) {
  const { data, isError } = queryResult
  const containerRef = React.useRef<HTMLDivElement>(null)
  const itemRefs = React.useRef(new Map<string, HTMLDivElement>())
  const didInitialScroll = React.useRef(false)
  const [isAtBottom, setIsAtBottom] = React.useState(false)
  const trackUserScroll = React.useRef(true)
  const prependSnapshot = React.useRef<{
    anchorId?: string
    anchorTop?: number
    scrollHeight: number
    scrollTop: number
  } | null>(null)
  const getItemIdRef = React.useRef(getItemId)
  const olderRequestPending = React.useRef(false)
  const newerRequestPending = React.useRef(false)
  const topEdgeArmed = React.useRef(true)
  const bottomEdgeArmed = React.useRef(true)
  const suppressEdgeRequests = React.useRef(false)
  const lastObservedScrollTop = React.useRef(0)
  getItemIdRef.current = getItemId

  // Pages and their rows come from the API newest-first. Reverse both for a
  // normal chronological DOM. This avoids column-reverse scroll semantics.
  const items = React.useMemo(() => {
    const pages = [...(data?.pages ?? [])].sort(
      (left, right) => right.pageNumber - left.pageNumber
    )
    const seen = new Set<string>()

    return pages.flatMap((page) =>
      [...page.data].reverse().filter((item) => {
        const id = getItemIdRef.current(
          item as ItemFromInfiniteQueryResult<TQueryResult>
        )
        if (seen.has(id)) return false
        seen.add(id)
        return true
      })
    ) as ItemFromInfiniteQueryResult<TQueryResult>[]
  }, [data?.pages])

  const olderRef = React.useRef(older)
  const newerRef = React.useRef(newer)
  olderRef.current = older
  newerRef.current = newer

  const fetchOlder = React.useCallback(() => {
    const container = containerRef.current
    const state = olderRef.current
    if (
      !container ||
      !state.hasMore ||
      state.isFetching ||
      olderRequestPending.current ||
      !topEdgeArmed.current
    )
      return

    const containerTop = container.getBoundingClientRect().top
    let visibleAnchor: { id: string; top: number } | undefined
    itemRefs.current.forEach((node, id) => {
      const rect = node.getBoundingClientRect()
      if (
        rect.bottom > containerTop &&
        (!visibleAnchor || rect.top < visibleAnchor.top + containerTop)
      ) {
        visibleAnchor = { id, top: rect.top - containerTop }
      }
    })

    topEdgeArmed.current = false
    olderRequestPending.current = true
    prependSnapshot.current = {
      anchorId: visibleAnchor?.id,
      anchorTop: visibleAnchor?.top,
      scrollHeight: container.scrollHeight,
      scrollTop: container.scrollTop,
    }
    void state.fetch().catch(() => {
      topEdgeArmed.current = true
      olderRequestPending.current = false
      prependSnapshot.current = null
    })
  }, [])

  const fetchNewer = React.useCallback(() => {
    const state = newerRef.current
    if (
      !state.hasMore ||
      state.isFetching ||
      newerRequestPending.current ||
      !bottomEdgeArmed.current
    )
      return
    bottomEdgeArmed.current = false
    newerRequestPending.current = true
    void state.fetch().then(
      () => {
        newerRequestPending.current = false
      },
      () => {
        bottomEdgeArmed.current = true
        newerRequestPending.current = false
      }
    )
  }, [])

  React.useEffect(() => {
    if (sendedMessage) {
      //scroll to bottom when a new message is sent
      const container = containerRef.current
      if (container) {
        container.scrollTop = container.scrollHeight
        setIsAtBottom(true)
        trackUserScroll.current = true
        didInitialScroll.current = true
      }
    }
  }, [sendedMessage])

  React.useLayoutEffect(() => {
    const container = containerRef.current
    if (!container || items.length === 0) return

    if (!didInitialScroll.current) {
      const anchor = initialAnchorId
        ? itemRefs.current.get(initialAnchorId)
        : undefined
      if (anchor) {
        const containerRect = container.getBoundingClientRect()
        const anchorRect = anchor.getBoundingClientRect()
        const desiredScrollTop =
          container.scrollTop +
          anchorRect.top +
          anchorRect.height / 2 -
          (containerRect.top + container.clientHeight / 2)

        container.scrollTop = desiredScrollTop
        // Being physically close to the bottom is not the same as the user
        // choosing the bottom. A short unread section must not enable sticky
        // scrolling when the newer page arrives.
        setIsAtBottom(false)
        trackUserScroll.current = false
      } else {
        container.scrollTop = container.scrollHeight
        setIsAtBottom(true)
        trackUserScroll.current = true
      }
      lastObservedScrollTop.current = container.scrollTop
      didInitialScroll.current = true
      return
    }

    if (prependSnapshot.current) {
      const snapshot = prependSnapshot.current
      prependSnapshot.current = null
      olderRequestPending.current = false
      const anchor = snapshot.anchorId
        ? itemRefs.current.get(snapshot.anchorId)
        : undefined

      suppressEdgeRequests.current = true
      if (anchor && snapshot.anchorTop !== undefined) {
        const currentTop =
          anchor.getBoundingClientRect().top -
          container.getBoundingClientRect().top
        container.scrollTop += currentTop - snapshot.anchorTop
      } else {
        container.scrollTop =
          snapshot.scrollTop + (container.scrollHeight - snapshot.scrollHeight)
      }
      setIsAtBottom(false)
      topEdgeArmed.current = container.scrollTop > 200
      lastObservedScrollTop.current = container.scrollTop
      window.requestAnimationFrame(() => {
        suppressEdgeRequests.current = false
      })
    } else if (isAtBottom) {
      container.scrollTop = container.scrollHeight
      lastObservedScrollTop.current = container.scrollTop
    }
  }, [initialAnchorId, items])

  React.useEffect(() => {
    const container = containerRef.current
    if (!container || !didInitialScroll.current) return

    const onScroll = () => {
      const previousScrollTop = lastObservedScrollTop.current
      const currentScrollTop = container.scrollTop
      lastObservedScrollTop.current = currentScrollTop
      const distanceToBottom =
        container.scrollHeight - currentScrollTop - container.clientHeight
      if (currentScrollTop > 200) topEdgeArmed.current = true
      if (distanceToBottom > 200) bottomEdgeArmed.current = true
      if (!trackUserScroll.current || suppressEdgeRequests.current) return
      setIsAtBottom(distanceToBottom <= BOTTOM_THRESHOLD)
      if (currentScrollTop < previousScrollTop && currentScrollTop <= 100)
        fetchOlder()
      if (currentScrollTop > previousScrollTop && distanceToBottom <= 100)
        fetchNewer()
    }
    const enableUserScrollTracking = (event: WheelEvent) => {
      trackUserScroll.current = true
      if (suppressEdgeRequests.current) return
      if (event.deltaY < 0 && container.scrollTop <= 100) fetchOlder()
      if (
        event.deltaY > 0 &&
        container.scrollHeight - container.scrollTop - container.clientHeight <=
          100
      )
        fetchNewer()
    }
    const enableScrollTracking = () => {
      trackUserScroll.current = true
    }

    container.addEventListener("scroll", onScroll, { passive: true })
    container.addEventListener("wheel", enableUserScrollTracking, {
      passive: true,
    })
    container.addEventListener("touchstart", enableScrollTracking, {
      passive: true,
    })
    container.addEventListener("pointerdown", enableScrollTracking)
    container.addEventListener("keydown", enableScrollTracking)
    return () => {
      container.removeEventListener("scroll", onScroll)
      container.removeEventListener("wheel", enableUserScrollTracking)
      container.removeEventListener("touchstart", enableScrollTracking)
      container.removeEventListener("pointerdown", enableScrollTracking)
      container.removeEventListener("keydown", enableScrollTracking)
    }
  }, [fetchNewer, fetchOlder])

  return (
    <ScrollContainer ref={containerRef} {...props}>
      {older.hasMore && !isError && (
        <div>
          {older.isFetching && (
            <Center py="xs">
              <Loader size="sm" />
            </Center>
          )}
        </div>
      )}
      {items.map((item, index) => {
        const id = getItemId(item)
        return (
          <React.Fragment key={id}>
            {divider?.(items[index - 1], item)}
            {anchorMarker?.beforeId === id && anchorMarker.content}
            {index > 0 &&
              anchorMarker?.afterId === getItemId(items[index - 1]) &&
              anchorMarker.content}
            <div
              ref={(node) => {
                if (node) itemRefs.current.set(id, node)
                else itemRefs.current.delete(id)
              }}
            >
              {renderItem({ entity: item, index, containerRef })}
            </div>
          </React.Fragment>
        )
      })}
      {!isAtBottom && (
        <Affix position={{ bottom: 60, right: 20 }}>
          <ActionIcon
            variant="default"
            bdrs="xl"
            size="lg"
            onClick={() => {
              if (
                queryResult.data?.pages.some((page) => page.pageNumber === 0)
              ) {
                containerRef.current?.scrollTo({
                  top: containerRef.current.scrollHeight,
                  behavior: "smooth",
                })
              } else {
                fetchFirstPage()

                containerRef.current?.scrollTo({
                  top: containerRef.current.scrollHeight,
                  behavior: "smooth",
                })
              }
            }}
          >
            <IconChevronDown />
          </ActionIcon>
        </Affix>
      )}
      {newer.hasMore && !isError && (
        <div>
          {newer.isFetching && (
            <Center py="xs">
              <Loader size="sm" />
            </Center>
          )}
        </div>
      )}
    </ScrollContainer>
  )
}
